import { computed, toValue } from 'vue'
import type { MaybeRefOrGetter, Ref } from 'vue'
import type { ChatbotProfileContext } from 'components/src/chat/chatbotProfileContext'
import { FRONTEND_URL } from '~/utils'

/**
 * Structural shape of the reactive session returned by `client.useSession()`.
 * Declared explicitly (rather than via `ReturnType`) because the better-auth
 * client type exposes a Promise-returning overload that breaks `.value` access.
 * The real session object carries more fields; we only declare what we use.
 */
interface ProfileSessionValue {
    data: {
        user: {
            id: string
            name: string
            email: string
            image?: string | null
            isAnonymous?: boolean | null
        }
    } | null
}
type SessionRef = Ref<ProfileSessionValue>

/**
 * Builds the {@link ChatbotProfileContext} the shared chat components inject to
 * render the in-widget profile panel. The widget shows user info in read-only
 * mode and links to the main site for profile management actions.
 */
export function useChatbotProfile(deps: {
    session: SessionRef
    signOut: () => Promise<void>
    /** Whether at least one auth method is enabled for the agent. */
    canSignIn?: MaybeRefOrGetter<boolean>
    /** Wiring for the GDPR "delete my conversations" action (B.3). */
    erasure?: {
        baseUrl: string
        headers: MaybeRefOrGetter<Record<string, string> | undefined>
        /** Cleanup after the backend deleted the anonymous account. */
        onAccountDeleted: () => Promise<void>
    }
}): ChatbotProfileContext {
    const { session, signOut, erasure } = deps

    const user = computed(() => {
        const sessionUser = session.value.data?.user
        if (!sessionUser || sessionUser.isAnonymous) {
            return null
        }
        return {
            id: sessionUser.id,
            name: sessionUser.name,
            email: sessionUser.email,
            image: sessionUser.image,
            isAnonymous: sessionUser.isAnonymous,
        }
    })

    const canManageProfile = computed(() => user.value !== null)

    const canSignIn = computed(() => toValue(deps.canSignIn) ?? false)

    const profileUrl = computed(() => `${FRONTEND_URL}/app/profile`)

    const eraseConversations = erasure
        ? async () => {
              const response = await fetch(
                  `${erasure.baseUrl}/api/v1/me/conversations/erasure`,
                  {
                      method: 'POST',
                      credentials: 'include',
                      headers: toValue(erasure.headers),
                  },
              )
              if (!response.ok) {
                  throw new Error(
                      `Conversations erasure failed (${response.status})`,
                  )
              }
              const result = (await response.json()) as {
                  accountDeleted: boolean
              }
              if (result.accountDeleted) {
                  await erasure.onAccountDeleted()
              }
              return result
          }
        : undefined

    return {
        user,
        canManageProfile,
        canSignIn,
        profileUrl,
        signOut,
        eraseConversations,
    }
}
